import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import { useDocViewer } from './Context';
import { isTablet, isMobile } from 'react-device-detect';
import SignModal from '../SignAll/SignModal';
import { useAuth } from '~/context/auth';
import SweetAlertLib from 'react-bootstrap-sweetalert';
import Lottie from 'lottie-react';
import esignAllDocsSuccessAnimation from '~/assets/animations/success-esign-all-docs.json';
import esignAllDocsErrorAnimation from '~/assets/animations/failed-esign-all-docs.json';

const SweetAlert = SweetAlertLib as any;

const DocViewerFooter = () => {

  const { user: authUser } = useAuth();

  /**
   * Context vars (see: Context.tsx)
   */
  const { isSignBusy, signDoc, canSign, readOnly, enableSigning,
    addedFields, currentSignerId, userReachedEndOfDoc, capturedSignatures,
    setCapturedSignatures, isInstantSigningInProgress,
    setIsInstantSigningInProgress, instantSignDoc, onSigningComplete, orderDoc,
    addedNotes, showNotes, setShowNotes } = useDocViewer();

  // the signature types to sign
  const [signaturesRequired, setSignaturesRequired] = useState<number[]>([]);
  // whether the signature types modals are open ( each signature type has it's own modal )
  const [signatureTypesModalsOpen, setSignatureTypesModalsOpen] = useState<Record<string, boolean>>({});
  // wheter auto signing modal is open
  const [isAutoSigningModalOpen, setIsAutoSigningModalOpen] = useState(false);
  // whether the instant signing is in progress
  const [isAutoSigningInProgress, setIsAutoSigningInProgress] = useState(false);
  // whether the instant signing is errored
  const [isAutoSigningErrored, setIsAutoSigningErrored] = useState(false);
  // track previous captured keys so we only run sign when user just applied a signature
  const prevCapturedKeysRef: any = useRef(null);

  /**
   * Checks whether we need to show the footer at all
   * @returns {boolean}
   */
  const hasFooter = () => readOnly || canSign();

  const targetFields = addedFields.filter(f => f.signerId == currentSignerId);

  const signedFields = targetFields.filter(f => !!f.signature);

  const isSigningComplete = signedFields.length == targetFields.length;

  //We check if there are no signature fields for the current signer
  const hasNoSignatures = targetFields.length === 0;

  // apply signatures and keep track of the signatures applied
  const applySignatures = (signature: any, type: any) => {
    // close the modals
    const signatureTypesObj: Record<string, boolean> = {};

    Object.keys(signatureTypesModalsOpen).forEach(type => (
      signatureTypesObj[type] = false as boolean
    ));
    setSignatureTypesModalsOpen(signatureTypesObj);
    // update the captured signatures
    setCapturedSignatures((signatures: Record<string, string>) => ({
      ...signatures,
      [type]: signature,
    }));
  }

  // function to open the first signature modal
  const openFirstSignatureModal = () => {
    if (signaturesRequired.length > 0) {
      const updatedModals: Record<string, boolean> = { ...signatureTypesModalsOpen };
      signaturesRequired.forEach(type => {
        if (updatedModals[type] === undefined) {
          updatedModals[type] = false as boolean;
        }
      });
      updatedModals[signaturesRequired[0]] = true as boolean;
      setSignatureTypesModalsOpen(updatedModals);
    }
  };

  // close all modals and erase signatures applied
  const signatureModalCloseHandler = () => {
    const signatureTypesObj: Record<string, boolean> = {};

    signaturesRequired.forEach(type => (
      signatureTypesObj[type] = false as boolean
    ));
    setSignatureTypesModalsOpen(signatureTypesObj);
    setIsInstantSigningInProgress(false);
  }

  const signContractHandler = async () => {
    if (hasNoSignatures || isSigningComplete) {
      enableSigning(hasNoSignatures || isSigningComplete);
      return;
    }
    if (orderDoc.isInstantSignEnabled && signaturesRequired.length === 0) {
      try {
        setIsAutoSigningInProgress(true);
        setIsAutoSigningModalOpen(true);
        await instantSignDoc();
      } catch (error) {
        setIsAutoSigningErrored(true);
      } finally {
        setIsAutoSigningInProgress(false);
      }
    } else {
      enableSigning(hasNoSignatures || isSigningComplete);
    }
  }

  // get the animation data based on the status of the instant signing
  const getAnimationData = () => {
    if (isAutoSigningErrored) {
      return esignAllDocsErrorAnimation;
    }
    return esignAllDocsSuccessAnimation;
  }

  useEffect(() => {
    // we get the signature types on the doc
    const signatureTypesOnDoc = addedFields.filter(field => field.signerType == authUser?.type).map(field => field.type);
    // we get the already captured signatures
    const alreadyCapturedSignatures = Object.keys(capturedSignatures);
    // we get the signatures required/missing
    const signaturesRequired = signatureTypesOnDoc.filter(type => !alreadyCapturedSignatures.includes(type.toString()));
    setSignaturesRequired(signaturesRequired);
  }, [addedFields, capturedSignatures]);

  // runs whenever `capturedSignatures` change (only when user applied a signature, not on initial render)
  useEffect(() => {
    if (!orderDoc.isInstantSignEnabled) return;

    const appliedSignatures = Object.keys(capturedSignatures);
    if (appliedSignatures.length === 0) {
      prevCapturedKeysRef.current = null;
      return;
    }

    const prevKeys = prevCapturedKeysRef.current;
    const justAppliedSignature = prevKeys !== null && (
      appliedSignatures.length > prevKeys.length ||
      appliedSignatures.some(k => !prevKeys.includes(k))
    );
    prevCapturedKeysRef.current = appliedSignatures;

    // skip on first run (initial render) – only react when user just added a signature
    if (!justAppliedSignature) return;

    const signatureTypesOnDoc = addedFields.filter(field => field.signerType == authUser?.type).map(field => field.type);
    const allRequiredApplied = signatureTypesOnDoc.length > 0 && signatureTypesOnDoc.every(type => appliedSignatures.includes(type.toString()));

    if (allRequiredApplied) {
      try {
        setIsAutoSigningInProgress(true);
        setIsAutoSigningModalOpen(true);
        instantSignDoc();
      } catch (error) {
        setIsAutoSigningErrored(true);
      } finally {
        setIsAutoSigningInProgress(false);
      }
      return;
    }

    // open the next modal if it is the case
    const nextSignaturesModals = signaturesRequired.filter(type => !appliedSignatures.includes(`${type}`));

    if (nextSignaturesModals.length) {
      setSignatureTypesModalsOpen(prev => ({ ...prev, [nextSignaturesModals[0]]: true }));
    }
  }, [capturedSignatures, addedFields, authUser?.type]);

  useEffect(() => {
    if (isInstantSigningInProgress) {
      prevCapturedKeysRef.current = []; // so first applied signature counts as "just applied"
      openFirstSignatureModal();
    }
  }, [isInstantSigningInProgress]);

  return <React.Fragment>
    {hasFooter() && <div className="doc-viewer-footer mb-3">
      {readOnly && <>
        {
          !isTablet &&
          <div className="e-sign-doc-info">
            <div className="info-icon"><i className="fas fa-info-circle"></i></div>
            <div className="info-text">
              Before you begin the e-signing process please review the contract carefully to make
              sure all the terms of the agreement are clear. Once ready, click Sign Contract. <i className="fas fa-arrow-right arrow-icon"></i>
            </div>
          </div>
        }
        <Button type="button" color="primary" onClick={signContractHandler} disabled={hasNoSignatures || isSigningComplete ? false : !(userReachedEndOfDoc || (isMobile && orderDoc?.numOfPages === 1))}>
          {hasNoSignatures || isSigningComplete ? 'View contract' : 'Sign contract'}
        </Button>
      </>
      }
      {!readOnly && canSign() && (
        <>
          {addedNotes.length > 0 && (
            <label className="toggle-notes-switch">
              <input
                type="checkbox"
                checked={showNotes}
                onChange={() => setShowNotes((prev: boolean) => !prev)}
              />
              <span className="toggle-notes-slider" />
              <span className="toggle-notes-label">Show Comments</span>
            </label>
          )}
          {hasNoSignatures ? (
            <div className="text-center">
              <div className="no-signature-info text-danger">
                <strong>There is no signature needed from you on this document.</strong>
              </div>
            </div>
          ) : (
            <Button type="button" color="primary" onClick={signDoc} disabled={isSignBusy}>
              {isSignBusy && <i className="mdi mdi-spin mdi-loading me-1" />}
              <span><span className="me-2">{signedFields.length} / {targetFields.length}</span> {isSigningComplete ? 'Complete Signing' : 'Signatures'}</span>
            </Button>
          )}
        </>
      )}
    </div>}
    {/* Signature types modals */}
    {
      signaturesRequired.map((signatureType, index) =>
        <React.Fragment key={index}>
          {
            signatureTypesModalsOpen[signatureType] &&
            <SignModal
              isOpen={signatureTypesModalsOpen[signatureType]}
              closeHandler={() => signatureModalCloseHandler()}
              signatureType={signatureType}
              applySignatures={applySignatures}
            />
          }
        </React.Fragment>
      )
    }
    {/* Sign all docs documents modal */}
    {isAutoSigningModalOpen && (
      <SweetAlert
        custom
        title=""
        confirmBtnBsStyle="primary"
        cancelBtnBsStyle="primary"
        cancelBtnCssClass="bg-secondary"
        btnSize="default"
        disabled={false}
        confirmBtnText="Confirm"
        closeOnClickOutside={false}
        showConfirm={true}
        showCancel={false}
        onConfirm={() => {
          if (isAutoSigningErrored) {
            setIsAutoSigningErrored(false);
            setIsAutoSigningModalOpen(false);
          } else {
            onSigningComplete()
          }
        }}
      >
        <div className="d-flex flex-column justify-content-center align-items-center m-3">
          <div className="lottie-container">
            <Lottie
              animationData={getAnimationData()}
              className="lottie-obj"
            />
          </div>
          {
            isAutoSigningInProgress ?
              <div className="font-size-15 fw-bold text-center text-primary mt-4"> Fill & Sign All is in progress. Please wait.</div>
              :
              <div className="font-size-15 fw-bold text-center text-primary mt-4">{isAutoSigningErrored ? 'The signing process has failed! Please try again.' : 'The signing process has finished! The document has been signed!'}</div>
          }
        </div>
      </SweetAlert>
    )}
  </React.Fragment>
}

export default DocViewerFooter;